/* Sets jQuery on window BEFORE any plugin imports.
   Imported first by every core entry so Porto theme.js + Bootstrap see window.jQuery.

   Why this exists (2026-05-13):
   In ESM, ALL imports are resolved depth-first BEFORE the module body executes.
   If you write:

       import $ from 'jquery';
       window.$ = window.jQuery = $;   // this LINE runs AFTER all imports below
       import 'bootstrap';              // <-- body executes before the assignment
       import '../../vendor/porto/js/theme.js';

   then the theme.js / bootstrap bodies run BEFORE the window.jQuery assignment,
   so `jQuery` is undefined inside theme.js — crash:
     "Uncaught ReferenceError: jQuery is not defined at theme.js:162".

   Moving the assignment into a dedicated module and importing it FIRST forces
   the side effect to execute before any other plugin imports in the entry. */
import $ from 'jquery';
window.$ = window.jQuery = $;

/* Shim for jQuery.browser — removed in jQuery 1.9+ but Porto theme.js v4.3 still uses it.
   Porto reads $.browser.chrome and $.browser.mobile at top level (lines ~421, ~4245, ~4317,
   ~4357, ~4402, ~4431, ~4907 of theme.js). Theme.js DOES try to initialize $.browser.mobile
   itself further down (line ~4887) but that runs AFTER line 4245 which throws:
     "Cannot set properties of undefined (setting 'chrome')"

   We pre-populate $.browser with simple UA-based detection so theme.js can safely
   read+write properties without crashing. */
if (typeof $.browser === 'undefined') {
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent || '').toLowerCase();
  $.browser = {
    chrome:  /chrome|chromium|crios/.test(ua) && !/edg\//.test(ua),
    firefox: /firefox|fxios/.test(ua),
    safari:  /safari/.test(ua) && !/chrome|chromium|crios/.test(ua),
    msie:    /msie|trident/.test(ua),
    opera:   /opr\//.test(ua),
    edge:    /edg\//.test(ua),
    webkit:  /webkit/.test(ua),
    mozilla: /firefox/.test(ua),
    mobile:  /android|bb\d+|meego|avantgo|bada\/|blackberry|blazer|compal|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|maemo|midp|mmp|mobile|netfront|opera m(ob|in)i|palm|phone|pixi|pocket|psp|series(4|6)0|symbian|treo|webos|windows (ce|phone)|xda|xiino/.test(ua),
    version: '',
  };
}

/* Shim for Modernizr — Porto theme.js v4.3 expects window.Modernizr at line ~4371:
     customScroll: ( !Modernizr.overflowscrolling && !isAndroid && $.fn.nanoScroller !== 'undefined')
   Without Modernizr loaded, theme.js throws:
     "Uncaught ReferenceError: Modernizr is not defined at theme.js:4373"

   Porto only reads ONE property: Modernizr.overflowscrolling (whether the browser supports
   -webkit-overflow-scrolling: touch — present on iOS Safari, absent elsewhere). The check
   `!Modernizr.overflowscrolling` is "this is NOT an iOS-style device", which on desktop is
   correctly true (use nanoScroller custom scrollbar). Stubbing it to `false` matches that
   intent without pulling in the full Modernizr build.

   Detection: iOS Safari support for `-webkit-overflow-scrolling` was removed in iOS 13+.
   So setting `false` (treat as non-iOS) is the right default everywhere modern. */
if (typeof window.Modernizr === 'undefined') {
  window.Modernizr = { overflowscrolling: false };
}

/* ────────────────────────────────────────────────────────────────────────────
   PART A — ALWAYS-NEEDED jQuery plugins for Porto theme.js core
   ────────────────────────────────────────────────────────────────────────────
   These plugins are called UNCONDITIONALLY from theme.js's auto-running
   `theme.Skeleton.initialize()` path (triggered by theme.init.js line 656):

     Skeleton.initialize() → build() → buildSidebarLeft() → $nano.nanoScroller(...)
                                     → buildSidebarRight() → $nano.nanoScroller(...)
                                     → buildContentMenu() → $nano.nanoScroller(...)

   theme.js line 4373 has a subtle Porto bug:
     customScroll: ( !Modernizr.overflowscrolling && !isAndroid && $.fn.nanoScroller !== 'undefined')
   That `!== 'undefined'` is comparing a function to a STRING literal — it's
   always truthy. So nanoScroller is ALWAYS invoked regardless of guard intent.

   nanoscroller (~6 KB) is therefore strictly mandatory. We also bundle
   jquery.appear and jquery.cookie because:
     - jquery.appear is referenced by PluginAnimate (used everywhere with
       [data-appear-animation]) and PluginChartCircular. Both are tiny (~1 KB)
       and Porto's stock layout includes them in every page. Bundling avoids
       per-view loader logic in @push.
     - jquery.cookie is not directly used by theme.js but Porto's optional
       state-persistence patterns expect it; harmless ~1 KB to keep in core.

   Total weight addition: ~10 KB minified+gzipped. Acceptable. */
import 'nanoscroller';   // $.fn.nanoScroller — sidebar custom scrollbar (MANDATORY)
import 'jquery.appear';  // $.fn.appear — scroll-into-view animations
import 'jquery.cookie';  // $.cookie / $.fn.cookie — state persistence

/* ────────────────────────────────────────────────────────────────────────────
   PART B — Stub fallbacks for LAZY-LOADED jQuery plugins
   ────────────────────────────────────────────────────────────────────────────
   theme.js + theme.init.js reference many jQuery plugins that we load lazily
   per-view via @push('admin-js') (datatables, select2, summernote, etc.).
   Porto's theme.init.js wraps EACH plugin block with `if ( $.isFunction($.fn[ X ]) )`
   so those blocks are correctly no-ops when the plugin isn't loaded.

   HOWEVER, some Plugin*.build() bodies inside theme.js DO call plugins
   directly (without an isFunction guard). Most are only reached when a matching
   DOM element exists AND init.js's outer guard found the plugin loaded — but
   defensively stubbing every potentially-missing plugin to a chainable no-op
   guarantees no TypeError can ever propagate from theme.js core paths.

   How it works:
     - We define a chainable no-op (returns `this`) for each plugin name
       theme.js might reference, ONLY IF it's not already defined.
     - When a view later loads the real plugin (e.g., vendor-datatables.js),
       that import OVERWRITES $.fn.dataTable / DataTable with the real function.
       jQuery plugin registration is just assignment, so this is safe.
     - When a view does NOT load the plugin, theme.js's call becomes a no-op
       instead of a TypeError.

   This is forward-compatible: adding a new lazy plugin doesn't require
   updating this list — only adding to the list prevents future TypeErrors. */
const LAZY_PLUGINS = [
  // DataTables
  'dataTable', 'DataTable',
  // Forms
  'select2', 'summernote', 'mask', 'maskedinput', 'maxlength', 'multiselect',
  'markdown', 'autosize', 'placeholder', 'fileupload', 'validate',
  // UI / interaction
  'magnificPopup', 'owlCarousel', 'isotope', 'lazyload',
  'fullCalendar', 'flatpickr', 'datepicker', 'bootstrapDP', 'timepicker',
  'slider', 'spinner', 'sortable', 'nestable', 'tagsinput',
  // Charts / visualizations
  'easyPieChart', 'sparkline', 'flot', 'chartist', 'gauge',
  'morris', 'liquidmeter', 'jqvmap', 'gmap3',
  // Misc
  'jstree', 'jcarousel', 'pnotify', 'plupload', 'snap', 'snazzy', 'dropzone',
  'colorpicker', 'fileinput',
];

LAZY_PLUGINS.forEach(function (name) {
  if (typeof $.fn[name] !== 'function') {
    /* Chainable no-op: returns the jQuery collection so chained calls like
       `$('.x').plugin().css({...})` continue to work without a TypeError.
       Real plugins overwrite this when their vendor bundle loads later. */
    $.fn[name] = function () { return this; };
  }
});
